import firebase from 'firebase/app';
import 'firebase/auth';
import { isEmpty } from 'lodash';
import User from 'types/User';
import fetchGuestToken from './UseCases/fetchGuestToken';
import config from './config';
import authorizedFetch from './authorizedFetch';

function isGuest(): boolean {
  const user = firebase.auth().currentUser;

  return !!user && !!user.email && isEmpty(user.providerData);
}

async function signInGuest({ email, uid }: { email: string; uid?: string }): Promise<void> {
  const tokenResponse = await fetchGuestToken({ email, uid });
  await firebase.auth().signInWithCustomToken(tokenResponse.token);
}

async function linkCredential(
  email: string,
  password: string
): Promise<firebase.auth.UserCredential> {
  const user = firebase.auth().currentUser;

  if (!user) {
    throw new Error();
  }

  // If a firebase user exists attempt to link the credential
  const credential = await firebase.auth.EmailAuthProvider.credential(email, password);

  return user.linkWithCredential(credential);
}

async function linkGuestUser(
  email: string,
  password: string
): Promise<firebase.auth.UserCredential> {
  // Use the token generator to refresh any stale tokens
  // first before linking credentials to avoid issues
  // with needing to re-authenticate
  await signInGuest({ email });
  return linkCredential(email, password);
}

const googleAuthProvider = (new firebase.auth.GoogleAuthProvider().addScope(
  'profile'
) as firebase.auth.GoogleAuthProvider).addScope('email');

const facebookAuthProvider = new firebase.auth.FacebookAuthProvider().addScope('email');

export async function sendPasswordResetEmail(email: string): Promise<void> {
  await firebase.auth().sendPasswordResetEmail(email);
}

export enum AuthProviders {
  Facebook = 'facebook',
  Google = 'google',
  Password = 'password',
  Guest = 'guest',
  Anon = 'anon',
}

async function ensureAutolistUser(email: string): Promise<void> {
  await authorizedFetch(`${config.coreApiUrl}/users/sign_in`, {
    method: 'POST',
    body: JSON.stringify({
      email,
    }),
  });
}

export async function authAndSignInWithProvider(
  authProvider: AuthProviders
): Promise<firebase.auth.UserCredential> {
  const provider =
    authProvider === AuthProviders.Facebook ? facebookAuthProvider : googleAuthProvider;

  const credential = await firebase.auth().signInWithPopup(provider);

  const email = credential.user?.email;

  if (!email) throw new Error('No email in auth credential');

  await ensureAutolistUser(email);

  return credential;
}

export async function createAccountWithEmailAndPassword(
  email: string,
  password: string
): Promise<firebase.auth.UserCredential> {
  if (isGuest()) {
    return linkGuestUser(email, password);
  }

  const credential = await firebase.auth().createUserWithEmailAndPassword(email, password);

  await ensureAutolistUser(email);

  return credential;
}

export async function signInWithEmailAndPassword(
  email: string,
  password: string
): Promise<firebase.auth.UserCredential> {
  if (isGuest()) {
    return linkGuestUser(email, password);
  }

  const credential = await firebase.auth().signInWithEmailAndPassword(email, password);

  await ensureAutolistUser(email);

  return credential;
}

interface OnAuthStateChangedCallback {
  (user?: User): void;
}

export function onAuthStateChanged(callback: OnAuthStateChangedCallback): () => void {
  return firebase.auth().onAuthStateChanged((user) => {
    // despite the type signature, the user can actuall be null
    if (!user) {
      callback();
      return;
    }

    const userInfo = {
      email: user.email as string,
      firebaseUid: user.uid,
    };
    callback(userInfo);
  });
}

export function getFirebaseUid(): string | undefined {
  return firebase.auth().currentUser?.uid;
}

enum AuthLevel {
  anon = 'anon',
  guest = 'guest',
  strong = 'strong',
}

export function getEventAuthLevel(): AuthLevel {
  let authLevel;
  const user = firebase.auth().currentUser;
  if (user && user.providerData && user.providerData.length > 0) {
    authLevel = AuthLevel.strong;
  } else if (user && user.isAnonymous) {
    authLevel = AuthLevel.anon;
  } else {
    authLevel = AuthLevel.guest;
  }
  return authLevel;
}

export function getEventAuthProviders(): AuthProviders[] {
  const user = firebase.auth().currentUser;
  if (!user) {
    return [];
  }
  if (!user.providerData) {
    return [];
  }

  const authProviders: AuthProviders[] = [];

  user.providerData.forEach((provider) => {
    if (!provider?.providerId) {
      return;
    }

    if (provider.providerId === firebase.auth.FacebookAuthProvider.PROVIDER_ID) {
      authProviders.push(AuthProviders.Facebook);
    } else if (provider.providerId === firebase.auth.GoogleAuthProvider.PROVIDER_ID) {
      authProviders.push(AuthProviders.Google);
    } else if (provider.providerId === firebase.auth.EmailAuthProvider.PROVIDER_ID) {
      authProviders.push(AuthProviders.Password);
    }
  });
  return authProviders;
}
